import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Clock, Search, Users } from 'lucide-react';
import {
	formatMoney,
	getRelativeTime,
	TimestampService,
	type ListTransactionsQueryType,
} from '@zlinkx/shared-v2';
import { useTranslation } from '@/i18n';
import { useCurrentMerchant } from '@/hooks-v2/context';
import { useMerchantMembers, useMerchantTransactions } from '@/hooks-v2/aggregator';

const MEMBER_SEARCH_DEBOUNCE_MS = 300;
const RECENT_TIMESTAMP_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

type ApiTransactionType = Exclude<NonNullable<ListTransactionsQueryType['type']>, 'refund'>;
type TypeFilterValue = 'all' | ApiTransactionType;
type MemberSuggestion = ReturnType<typeof useMerchantMembers>['data']['members'][number];

const typeFilterOptions: Array<{
	value: TypeFilterValue;
	translationKey: string;
	fallbackLabel: string;
}> = [
	{ value: 'all', translationKey: 'merchant.transactions.typeOptions.all', fallbackLabel: 'All' },
	{
		value: 'redemption',
		translationKey: 'merchant.transactions.typeOptions.redemption',
		fallbackLabel: 'Redemption',
	},
	{
		value: 'prepaid_deposit',
		translationKey: 'merchant.transactions.typeOptions.prepaidDeposit',
		fallbackLabel: 'Deposit',
	},
	{
		value: 'credit_adjustment',
		translationKey: 'merchant.transactions.typeOptions.creditAdjustment',
		fallbackLabel: 'Credit Adjustment',
	},
	{
		value: 'credit_usage',
		translationKey: 'merchant.transactions.typeOptions.creditUsage',
		fallbackLabel: 'Credit Usage',
	},
];

function interpolateFallback(
	template: string,
	params?: Record<string, string | number>,
): string {
	if (!params) {
		return template;
	}

	return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
		const value = params[key];
		return value === undefined ? match : String(value);
	});
}

function isSupportedType(value: string | null): value is TypeFilterValue {
	if (value === 'all') {
		return true;
	}

	return value === 'redemption' || value === 'prepaid_deposit' || value === 'credit_adjustment' || value === 'credit_usage';
}

function toDateInputValue(isoValue: string): string {
	if (isoValue.length === 0) {
		return '';
	}

	const parsed = TimestampService.parseDateTimeInput(isoValue);
	if (!TimestampService.isValidDate(parsed)) {
		return '';
	}

	const defaults = TimestampService.getDisplayDefaults();
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: defaults.timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).formatToParts(parsed);
	const year = parts.find(part => part.type === 'year')?.value;
	const month = parts.find(part => part.type === 'month')?.value;
	const day = parts.find(part => part.type === 'day')?.value;

	if (!year || !month || !day) {
		return '';
	}

	return `${year}-${month}-${day}`;
}

function toIsoFromDateInput(value: string, options: { endOfDay?: boolean } = {}): string | null {
	if (value.length === 0) {
		return null;
	}

	const parsed = TimestampService.parseDateTimeInput(value);
	if (!TimestampService.isValidDate(parsed)) {
		return null;
	}

	if (options.endOfDay && DATE_ONLY_INPUT_PATTERN.test(value)) {
		parsed.setHours(23, 59, 59, 999);
	}

	return TimestampService.toIsoString(parsed);
}

function formatTransactionTimestamp(value: string, invalidDateLabel: string): string {
	const parsed = TimestampService.parseDateTimeInput(value);
	if (!TimestampService.isValidDate(parsed)) {
		return invalidDateLabel;
	}

	const elapsedMs = TimestampService.getMillisecondsSince(parsed);
	if (elapsedMs <= RECENT_TIMESTAMP_THRESHOLD_MS) {
		return getRelativeTime(parsed);
	}

	return TimestampService.formatForDisplay(parsed, { style: 'medium' });
}

function formatSignedAmount(amountCents: number): string {
	const sign = amountCents >= 0 ? '+' : '-';
	const absoluteCents = Math.abs(amountCents);
	return `${sign}${formatMoney(absoluteCents)}`;
}

function toShortDetails(text: string): string {
	if (text.length <= 90) {
		return text;
	}

	return `${text.slice(0, 87)}...`;
}

const TransactionHistory: React.FC = () => {
	const { t } = useTranslation();
	const { selectedMerchant, selectedMerchantId, loading: isLoadingMerchant } = useCurrentMerchant();
	const [searchParams, setSearchParams] = useSearchParams();

	const translate = useCallback((
		key: string,
		fallback: string,
		params?: Record<string, string | number>,
	): string => {
		const translated = t(key, params);
		if (translated !== key) {
			return translated;
		}

		return interpolateFallback(fallback, params);
	}, [t]);

	const typeParam = searchParams.get('type');
	const memberParam = searchParams.get('member') ?? '';
	const startDateParam = searchParams.get('startDate') ?? '';
	const endDateParam = searchParams.get('endDate') ?? '';

	const selectedType: TypeFilterValue = isSupportedType(typeParam) ? typeParam : 'all';

	const [memberInput, setMemberInput] = useState(memberParam);
	const [debouncedMemberInput, setDebouncedMemberInput] = useState(memberParam);
	const [showMemberSuggestions, setShowMemberSuggestions] = useState(false);

	useEffect(() => {
		const timeoutId = window.setTimeout(() => {
			setDebouncedMemberInput(memberInput.trim());
		}, MEMBER_SEARCH_DEBOUNCE_MS);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [memberInput]);

	useEffect(() => {
		if (memberParam.length > 0 && memberInput.length === 0) {
			setMemberInput(memberParam);
		}
	}, [memberInput.length, memberParam]);

	const updateSearchParams = (updates: {
		type?: TypeFilterValue | null;
		member?: string | null;
		startDate?: string | null;
		endDate?: string | null;
	}) => {
		setSearchParams(
			currentParams => {
				const next = new URLSearchParams(currentParams);
				const entries = Object.entries(updates);

				entries.forEach(([key, value]) => {
					if (value === null || value === undefined || value.length === 0) {
						next.delete(key);
						return;
					}
					next.set(key, value);
				});

				return next;
			},
			{ replace: true },
		);
	};

	const transactionQuery = useMemo<ListTransactionsQueryType>(() => {
		const query: ListTransactionsQueryType = {
			limit: 50,
		};

		if (selectedType !== 'all') {
			query.type = selectedType;
		}

		if (memberParam.length > 0) {
			query.userId = memberParam;
		}

		if (startDateParam.length > 0) {
			query.startDate = startDateParam;
		}

		if (endDateParam.length > 0) {
			query.endDate = endDateParam;
		}

		return query;
	}, [endDateParam, memberParam, selectedType, startDateParam]);

	const transactions = useMerchantTransactions(
		{
			merchantId: selectedMerchantId ?? 0,
			query: transactionQuery,
		},
	);

	const shouldSearchMembers =
		selectedMerchantId !== null && showMemberSuggestions && debouncedMemberInput.length > 0;

	const memberSearch = useMerchantMembers({
		merchantId: selectedMerchantId ?? 0,
		enabled: shouldSearchMembers,
		filters: {
			search: debouncedMemberInput,
		},
		queryParams: {
			limit: 10,
			search: debouncedMemberInput,
		},
	});

	const memberSuggestions = useMemo(() => {
		if (!shouldSearchMembers) {
			return [];
		}

		return memberSearch.data.members.slice(0, 10);
	}, [memberSearch.data.members, shouldSearchMembers]);

	const handleTypeChange = (value: string) => {
		if (!isSupportedType(value)) {
			return;
		}

		if (value === 'all') {
			updateSearchParams({ type: null });
			return;
		}

		updateSearchParams({ type: value });
	};

	const handleMemberInputChange = (value: string) => {
		setMemberInput(value);
		setShowMemberSuggestions(value.trim().length > 0);

		if (memberParam.length > 0) {
			updateSearchParams({ member: null });
		}
	};

	const handleMemberSelect = (member: MemberSuggestion) => {
		const label = member.displayName ?? member.phoneMasked;
		setMemberInput(`${label} (${member.phoneMasked})`);
		setShowMemberSuggestions(false);
		updateSearchParams({ member: member.userId });
	};

	const handleStartDateChange = (value: string) => {
		updateSearchParams({ startDate: toIsoFromDateInput(value) });
	};

	const handleEndDateChange = (value: string) => {
		updateSearchParams({ endDate: toIsoFromDateInput(value, { endOfDay: true }) });
	};

	const typeLabelMap = useMemo<Record<ApiTransactionType | 'refund', string>>(
		() => ({
			redemption: translate(
				'merchant.transactions.typeOptions.redemption',
				'Redemption',
			),
			prepaid_deposit: translate(
				'merchant.transactions.typeOptions.prepaidDeposit',
				'Deposit',
			),
			credit_adjustment: translate(
				'merchant.transactions.typeOptions.creditAdjustment',
				'Credit Adjustment',
			),
			credit_usage: translate(
				'merchant.transactions.typeOptions.creditUsage',
				'Credit Usage',
			),
			refund: translate('merchant.transactions.typeOptions.refund', 'Refund'),
		}),
		[translate],
	);
	const invalidDateLabel = translate('common.invalidDate', 'Invalid date');

	if (isLoadingMerchant || transactions.isLoading) {
		return (
			<div
				className='bg-white rounded-xl border border-gray-200 p-8 text-center'
				data-testid='transaction-history-loading'
			>
				<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3' />
				<p className='text-gray-600'>
					{translate('merchant.transactions.states.loading', 'Loading transactions...')}
				</p>
			</div>
		);
	}

	if (selectedMerchant === null) {
		return (
			<div className='bg-white rounded-xl border border-gray-200 p-8 text-center'>
				<p className='text-gray-700'>
					{translate(
						'merchant.transactions.states.noMerchant',
						'Please select a merchant to view transactions.',
					)}
				</p>
			</div>
		);
	}

	const isEmpty = transactions.data.length === 0;

	return (
		<div className='space-y-6'>
			<div className='bg-white rounded-xl border border-gray-200 p-6'>
				<div className='flex items-start justify-between gap-4'>
					<div>
						<h1 className='text-2xl font-semibold text-gray-900'>
							{translate('merchant.transactions.title', 'Transaction History')}
						</h1>
						<p className='text-gray-600 mt-1'>
							{translate(
								'merchant.transactions.subtitle',
								'Track redemptions, deposits, and credit movements for {{merchantName}}',
								{ merchantName: selectedMerchant.merchantName },
							)}
						</p>
					</div>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-4 gap-3 mt-6'>
					<div>
						<label className='block text-sm font-medium text-gray-700 mb-1'>
							{translate('merchant.transactions.filters.type', 'Type')}
						</label>
						<select
							data-testid='transaction-type-filter'
							value={selectedType}
							onChange={event => {
								handleTypeChange(event.target.value);
							}}
							className='w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800'
						>
							{typeFilterOptions.map(option => (
								<option key={option.value} value={option.value}>
									{translate(option.translationKey, option.fallbackLabel)}
								</option>
							))}
						</select>
					</div>

					<div className='relative'>
						<label className='block text-sm font-medium text-gray-700 mb-1'>
							{translate('merchant.transactions.filters.member', 'Member')}
						</label>
						<div className='relative'>
							<Search className='w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2' />
							<input
								data-testid='transaction-member-input'
								type='text'
								value={memberInput}
								onFocus={() => {
									setShowMemberSuggestions(memberInput.trim().length > 0);
								}}
								onChange={event => {
									handleMemberInputChange(event.target.value);
								}}
								placeholder={translate(
									'merchant.transactions.filters.memberPlaceholder',
									'Search by name or phone',
								)}
								className='w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-gray-800'
							/>
						</div>

						{showMemberSuggestions && memberSuggestions.length > 0 && (
							<div className='absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto'>
								{memberSuggestions.map(member => (
									<button
										key={member.userId}
										type='button'
										data-testid={`transaction-member-option-${member.userId}`}
										onMouseDown={event => {
											event.preventDefault();
											handleMemberSelect(member);
										}}
										className='w-full text-left px-3 py-2 hover:bg-gray-50'
									>
										<p className='text-sm font-medium text-gray-900'>
											{member.displayName ?? translate('common.notSet', 'Not set')}
										</p>
										<p className='text-xs text-gray-500'>{member.phoneMasked}</p>
									</button>
								))}
							</div>
						)}
					</div>

					<div>
						<label className='block text-sm font-medium text-gray-700 mb-1'>
							{translate('merchant.transactions.filters.startDate', 'Start date')}
						</label>
						<input
							data-testid='transaction-start-date-filter'
							type='date'
							value={toDateInputValue(startDateParam)}
							onChange={event => {
								handleStartDateChange(event.target.value);
							}}
							className='w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-800'
						/>
					</div>

					<div>
						<label className='block text-sm font-medium text-gray-700 mb-1'>
							{translate('merchant.transactions.filters.endDate', 'End date')}
						</label>
						<input
							data-testid='transaction-end-date-filter'
							type='date'
							value={toDateInputValue(endDateParam)}
							onChange={event => {
								handleEndDateChange(event.target.value);
							}}
							className='w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-800'
						/>
					</div>
				</div>
			</div>

			<div className='bg-white rounded-xl border border-gray-200'>
				{transactions.isError && (
					<div
						data-testid='transaction-history-error'
						className='m-4 border border-red-200 bg-red-50 rounded-lg p-4 text-red-800'
					>
						<div className='flex items-start gap-2'>
							<AlertCircle className='w-5 h-5 mt-0.5' />
							<div>
								<p className='font-medium'>
									{translate(
										'merchant.transactions.states.errorTitle',
										'Unable to load transactions',
									)}
								</p>
								<p className='text-sm'>
									{transactions.error?.message ??
										translate(
											'merchant.transactions.states.errorDescription',
											'Please try again in a moment.',
										)}
								</p>
							</div>
						</div>
					</div>
				)}

				{!transactions.isError && isEmpty && (
					<div className='p-10 text-center'>
						<div className='w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3'>
							<Users className='w-6 h-6 text-gray-500' />
						</div>
						<p className='text-lg font-medium text-gray-900'>
							{translate('merchant.transactions.states.emptyTitle', 'No transactions yet')}
						</p>
						<p className='text-gray-600 mt-1'>
							{translate(
								'merchant.transactions.states.emptyDescription',
								'Transactions from redemptions, deposits, and adjustments will appear here',
							)}
						</p>
					</div>
				)}

				{!transactions.isError && !isEmpty && (
					<>
						<ul className='divide-y divide-gray-200'>
							{transactions.data.map(transaction => (
								<li key={`${transaction.type}-${transaction.transaction_id}`} className='p-4 sm:p-5'>
									<div className='flex items-start justify-between gap-4'>
										<div className='min-w-0'>
											<p className='text-sm font-semibold text-gray-900'>
												{typeLabelMap[transaction.type]}
											</p>
											<p className='text-sm text-gray-600 mt-0.5'>
												{transaction.user_display_name ??
													translate('merchant.transactions.unknownMember', 'Unknown member')}
											</p>
											<p className='text-sm text-gray-500 mt-1 break-words'>
												{toShortDetails(transaction.description)}
											</p>
										</div>

										<div className='text-right shrink-0'>
											<p
												className={`text-sm font-semibold ${
													transaction.amount >= 0 ? 'text-green-700' : 'text-red-700'
												}`}
											>
												{formatSignedAmount(transaction.amount)}
											</p>
											<p className='text-xs text-gray-500 mt-1 inline-flex items-center gap-1'>
												<Clock className='w-3.5 h-3.5' />
												{formatTransactionTimestamp(transaction.timestamp, invalidDateLabel)}
											</p>
										</div>
									</div>
								</li>
							))}
						</ul>

						<div className='px-4 py-4 border-t border-gray-200 flex items-center justify-between gap-3'>
							<span className='text-sm text-gray-600'>
								{transactions.hasNextPage
									? translate('merchant.transactions.pagination.moreAvailable', 'More results available')
									: translate('merchant.transactions.pagination.endOfList', 'End of transaction history')}
							</span>

							{transactions.hasNextPage && (
								<button
									type='button'
									data-testid='transaction-load-more'
									onClick={() => {
										transactions.fetchNextPage();
									}}
									disabled={transactions.isFetchingNextPage}
									className='px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed'
								>
									{transactions.isFetchingNextPage
										? translate('merchant.transactions.pagination.loadingMore', 'Loading...')
										: translate('merchant.transactions.pagination.loadMore', 'Load more')}
								</button>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default TransactionHistory;
